import React, { useEffect, useState } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { Badge, Box, Button, Checkbox, Flex, Group, Modal, Pagination, Select, Table, Text, TextInput, LoadingOverlay, ScrollArea, Stack, Textarea, ActionIcon } from '@mantine/core'
import { IconPlus, IconSearch, IconEdit, IconTrash } from '@tabler/icons-react'
import { useDebouncedCallback } from '@mantine/hooks'
import { createWarehouse, getAllWarehouseList, deleteWarehouse, updateWarehouse } from '../../../../services/warehouse'
import { getAllCityList, getAllCountryList, getAllDistrictList, getAllProvinceList } from '../../../../services/location'
import { completedLoadingNotification, failedLoadingNotification, loadingNotification } from '../../../../component/ui/Notifications'
import { openConfirmModal } from '@mantine/modals'

const ModalAddWarehouse = ({ opened, onClose, onSuccess, data }) => {

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    address_2: '',
    country_id: null,
    province_id: null,
    city_id: null,
    district_id: null,
    owned_by_ali: false,
    is_private: false,
    pic_name: '',
    pic_phone: '',
    pic_email: ''
  })

  // Location Data
  const [countries, setCountries] = useState([])
  const [provinces, setProvinces] = useState([])
  const [cities, setCities] = useState([])
  const [districts, setDistricts] = useState([])

  const [loadingLocation, setLoadingLocation] = useState({
    country: false,
    province: false,
    city: false,
    district: false
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (opened) {
      fetchCountries()
      if (data) {
        // Edit Mode
        setFormData({
          name: data.attributes.name || '',
          address: data.attributes.address || '',
          address_2: data.attributes.address_2 || '',
          country_id: data.relationships?.country?.id ? String(data.relationships.country.id) : null,
          province_id: data.relationships?.province?.id ? String(data.relationships.province.id) : null,
          city_id: data.relationships?.city?.id ? String(data.relationships.city.id) : null,
          district_id: data.relationships?.district?.id ? String(data.relationships.district.id) : null,
          owned_by_ali: data.attributes.owned_by_ali || false,
          is_private: data.attributes.is_private || false,
          pic_name: data.attributes.pic_details?.find(p => p.label === 'name')?.value || '',
          pic_phone: data.attributes.pic_details?.find(p => p.label === 'phone')?.value || '',
          pic_email: data.attributes.pic_details?.find(p => p.label === 'email')?.value || ''
        })

        // Pre-fetch location data if IDs exist
        if (data.relationships?.country?.id) fetchProvinces(data.relationships.country.id)
        if (data.relationships?.province?.id) fetchCities(data.relationships.province.id)
        if (data.relationships?.city?.id) fetchDistricts(data.relationships.city.id)

      } else {
        // Add Mode - Reset
        setFormData({
          name: '',
          address: '',
          address_2: '',
          country_id: null,
          province_id: null,
          city_id: null,
          district_id: null,
          owned_by_ali: false,
          is_private: false,
          pic_name: '',
          pic_phone: '',
          pic_email: ''
        })
      }
      setErrors({})
    }
  }, [opened, data])

  const fetchCountries = async () => {
    setLoadingLocation(prev => ({ ...prev, country: true }))
    try {
      const response = await getAllCountryList({ 'page[limit]': 1000 })
      if (response && response.data) {
        setCountries(response.data.map(c => ({ value: String(c.id), label: c.attributes.name })))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingLocation(prev => ({ ...prev, country: false }))
    }
  }

  const fetchProvinces = async (countryId) => {
    setLoadingLocation(prev => ({ ...prev, province: true }))
    try {
      const response = await getAllProvinceList({ 'filter[country_id]': countryId, 'page[limit]': 1000 })
      if (response && response.data) {
        setProvinces(response.data.map(p => ({ value: String(p.id), label: p.attributes.name })))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingLocation(prev => ({ ...prev, province: false }))
    }
  }

  const fetchCities = async (provinceId) => {
    setLoadingLocation(prev => ({ ...prev, city: true }))
    try {
      const response = await getAllCityList({ 'filter[province_id]': provinceId, 'page[limit]': 1000 })
      if (response && response.data) {
        setCities(response.data.map(c => ({ value: String(c.id), label: c.attributes.name })))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingLocation(prev => ({ ...prev, city: false }))
    }
  }

  const fetchDistricts = async (cityId) => {
    setLoadingLocation(prev => ({ ...prev, district: true }))
    try {
      const response = await getAllDistrictList({ 'filter[city_id]': cityId, 'page[limit]': 1000 })
      if (response && response.data) {
        setDistricts(response.data.map(d => ({ value: String(d.id), label: d.attributes.name })))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingLocation(prev => ({ ...prev, district: false }))
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }

      // Reset Logic
      if (field === 'country_id') {
        newData.province_id = null
        newData.city_id = null
        newData.district_id = null
        setProvinces([])
        setCities([])
        setDistricts([])
        if (value) fetchProvinces(value)
      }
      if (field === 'province_id') {
        newData.city_id = null
        newData.district_id = null
        setCities([])
        setDistricts([])
        if (value) fetchCities(value)
      }
      if (field === 'city_id') {
        newData.district_id = null
        setDistricts([])
        if (value) fetchDistricts(value)
      }
      return newData
    })

    // Clear error for the field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name) {
      newErrors.name = 'Warehouse Name is required'
    } else if (formData.name.length < 5) {
      newErrors.name = 'Warehouse Name must be at least 5 characters'
    }

    if (!formData.address) {
      newErrors.address = 'Address is required'
    } else if (formData.address.length < 5) {
      newErrors.address = 'Address must be at least 5 characters'
    }

    if (!formData.country_id) newErrors.country_id = 'Country is required'
    if (!formData.province_id) newErrors.province_id = 'Province is required'
    if (!formData.city_id) newErrors.city_id = 'City is required'
    if (!formData.pic_name) newErrors.pic_name = 'PIC Name is required'
    if (!formData.pic_phone) newErrors.pic_phone = 'PIC Phone is required'
    if (!formData.pic_email) newErrors.pic_email = 'PIC Email is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    const action = data ? 'update-warehouse' : 'create-warehouse'
    const title = data ? 'Updating Warehouse' : 'Creating Warehouse'

    loadingNotification(action, title, 'Please wait...')
    try {
      const { pic_name, pic_phone, pic_email, ...rest } = formData

      const payload = {
        ...rest,
        pic_details: JSON.stringify([
          { label: 'name', value: pic_name },
          { label: 'phone', value: pic_phone },
          { label: 'email', value: pic_email }
        ]),
        country_id: Number(formData.country_id),
        province_id: Number(formData.province_id),
        city_id: Number(formData.city_id),
        district_id: formData.district_id ? Number(formData.district_id) : null,
      }

      if (data) {
        await updateWarehouse(data.id, payload)
        completedLoadingNotification(action, 'Success', 'Warehouse updated successfully')
      } else {
        await createWarehouse(payload)
        completedLoadingNotification(action, 'Success', 'Warehouse created successfully')
      }

      onSuccess()
      onClose()
    } catch (error) {
      failedLoadingNotification(action, 'Failed', error?.response?.data?.message || 'Something went wrong')
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={data ? "Edit Warehouse" : "Add New Warehouse"} size="lg">
      <Stack gap="md">
        <TextInput
          label="Warehouse Name"
          placeholder="Enter warehouse name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          withAsterisk
          error={errors.name}
        />
        <Textarea
          label="Address"
          placeholder="Enter address"
          value={formData.address}
          onChange={(e) => handleChange('address', e.target.value)}
          withAsterisk
          error={errors.address}
        />


        <Box>
          <Text size="sm" fw={500} mb="xs">PIC Details</Text>
          <Group grow>
            <TextInput
              label="PIC Name"
              placeholder="Enter name"
              withAsterisk
              error={errors.pic_name}
              value={formData.pic_name}
              onChange={(e) => handleChange('pic_name', e.target.value)}
            />
            <TextInput
              label="PIC Phone"
              placeholder="Enter phone"
              withAsterisk
              error={errors.pic_phone}
              value={formData.pic_phone}
              onChange={(e) => handleChange('pic_phone', e.target.value)}
            />
          </Group>
          <TextInput
            mt="sm"
            label="PIC Email"
            placeholder="Enter email"
            withAsterisk
            error={errors.pic_email}
            value={formData.pic_email}
            onChange={(e) => handleChange('pic_email', e.target.value)}
          />
        </Box>
        <Textarea
          label="Warehouse Alternative Address"
          value={formData.address_2}
          onChange={(e) => handleChange('address_2', e.target.value)}
        />
        <Group grow>
          <Select
            label="Country"
            placeholder="Select country"
            data={countries}
            value={formData.country_id}
            onChange={(val) => handleChange('country_id', val)}
            searchable
            disabled={loadingLocation.country}
            withAsterisk
            error={errors.country_id}
          />
          <Select
            label="Province"
            placeholder="Select province"
            data={provinces}
            value={formData.province_id}
            onChange={(val) => handleChange('province_id', val)}
            searchable
            disabled={!formData.country_id || loadingLocation.province}
            withAsterisk
            error={errors.province_id}
          />
        </Group>

        <Group grow>
          <Select
            label="City"
            placeholder="Select city"
            data={cities}
            value={formData.city_id}
            onChange={(val) => handleChange('city_id', val)}
            searchable
            disabled={!formData.province_id || loadingLocation.city}
            withAsterisk
            error={errors.city_id}
          />
          <Select
            label="District"
            placeholder="Select district"
            data={districts}
            value={formData.district_id}
            onChange={(val) => handleChange('district_id', val)}
            searchable
            disabled={!formData.city_id || loadingLocation.district}
          />
        </Group>

        <Group>
          <Checkbox
            label="Owned by ALI"
            checked={formData.owned_by_ali}
            onChange={(e) => handleChange('owned_by_ali', e.currentTarget.checked)}
          />
          <Checkbox
            label="Private Warehouse"
            checked={formData.is_private}
            onChange={(e) => handleChange('is_private', e.currentTarget.checked)}
          />
        </Group>



        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{data ? 'Update Warehouse' : 'Create Warehouse'}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}




const WarehouseList = () => {
  const dispatch = useDispatch()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedData, setSelectedData] = useState(null)

  const [params, setParams] = useState({
    'page[num]': 1,
    'page[limit]': 10,
    'include': 'country,province,city,districts', // user asked for district but service usually 'districts' or 'district'. User request says 'district'. I'll stick to 'district' if that matches the Include in API usually, but user specific 'country,province,city,district'.
    // NOTE: User said `include=country,province,city,district`. I will use exactly that.
    'filter[name][like]': ''
  })

  const mappingRoute = [
    { label: '/', route: '/' },
    { label: 'Product', route: null },
    { label: 'Warehouse List', route: null },
  ]

  useEffect(() => {
    dispatch(updateRoute({ 'data': mappingRoute }))
    // eslint-disable-next-line
  }, [dispatch])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getAllWarehouseList({ ...params, include: 'country,province,city,district' })
      if (response && response.data) {
        // Based on AllListProduct, included is an object map: { type: { id: { attributes: ... } } }
        const included = response.included || {}
        const { country, province, city, district } = included

        const mappedData = response.data.map(item => {
          const rels = item.relationships || {}

          const countryId = rels.country?.id
          const provinceId = rels.province?.id
          const cityId = rels.city?.id
          const districtId = rels.district?.id
          // safely access the map if it exists
          const countryObj = country && country[countryId]
          const provinceObj = province && province[provinceId]
          const cityObj = city && city[cityId]
          const districtObj = district && district[districtId]

          return {
            ...item,
            country_name: countryObj?.attributes?.name,
            province_name: provinceObj?.attributes?.name,
            city_name: cityObj?.attributes?.name,
            district_name: districtObj?.attributes?.name,
          }
        })

        setData(mappedData)
        setTotal(response.meta?.pagination?.total || 0)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line
  }, [params])

  const handleSearch = useDebouncedCallback((query) => {
    setParams(prev => ({ ...prev, 'filter[name][like]': query, 'page[num]': 1 }))
  }, 500)

  const handlePageChange = (page) => {
    setParams(prev => ({ ...prev, 'page[num]': page }))
  }

  const handleEdit = (item) => {
    setSelectedData(item)
    setModalOpen(true)
  }

  const handleDelete = (item) => {
    openConfirmModal({
      title: 'Delete Warehouse',
      children: <Text size="sm">Are you sure you want to delete <b>{item.attributes.name}</b>?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        loadingNotification('delete-warehouse', 'Deleting Warehouse', 'Please wait...')
        try {
          await deleteWarehouse(item.id)
          completedLoadingNotification('delete-warehouse', 'Success', 'Warehouse deleted successfully')
          fetchData()
        } catch (error) {
          failedLoadingNotification('delete-warehouse', 'Failed', error?.response?.data?.message || 'Something went wrong')
        }
      }
    })
  }

  const rows = data.map((item) => {
    const attrs = item.attributes || {}

    return (
      <Table.Tr key={item.id}>
        <Table.Td>{attrs.name}</Table.Td>
        <Table.Td>{attrs.address}</Table.Td>
        <Table.Td>{item.country_name || '-'}</Table.Td>
        <Table.Td>{item.province_name || '-'}</Table.Td>
        <Table.Td>{item.city_name || '-'}</Table.Td>
        <Table.Td>{item.district_name || '-'}</Table.Td>
        <Table.Td>
          <Box>
            {attrs.pic_details.map((p, i) => (
              <Text key={i} size="xs">{p.label}: {p.value}</Text>
            ))}
          </Box>
        </Table.Td>
        <Table.Td>
          {attrs.is_private ? <Badge color="orange">Private</Badge> : <Badge color="green">Public</Badge>}
        </Table.Td>
        <Table.Td>
          {attrs.owned_by_ali ? <Badge color="blue">Yes</Badge> : <Badge color="gray">No</Badge>}
        </Table.Td>
        <Table.Td>
          <Group gap="xs">
            <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(item)}>
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(item)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <AuthLayout>
      <Box mb="lg">
        <Flex justify="space-between" align="center">
          <Text size="xl" fw={700}>Warehouse List</Text>
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setSelectedData(null); setModalOpen(true) }}>Add New Warehouse</Button>
        </Flex>

        <Group mt="md">
          <TextInput
            placeholder="Search warehouse..."
            leftSection={<IconSearch size={16} />}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Group>

        <Box pos="relative" mt="lg">
          <LoadingOverlay visible={loading} zIndex={100} overlayProps={{ radius: "sm", blur: 2 }} />
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Address</Table.Th>
                  <Table.Th>Country</Table.Th>
                  <Table.Th>Province</Table.Th>
                  <Table.Th>City</Table.Th>
                  <Table.Th>Subdistrict</Table.Th>
                  <Table.Th>PIC Detail</Table.Th>
                  <Table.Th w={100}>Private Warehouse</Table.Th>
                  <Table.Th>Owned by ALI</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length > 0 ? rows : (
                  <Table.Tr>
                    <Table.Td colSpan={10} align="center">
                      <Text c="dimmed">No data found</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>

        <Flex justify="flex-end" mt="md">
          <Pagination
            total={Math.ceil(total / params['page[limit]'])}
            value={params['page[num]']}
            onChange={handlePageChange}
          />
        </Flex>
      </Box>

      <ModalAddWarehouse
        opened={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedData(null) }}
        onSuccess={fetchData}
        data={selectedData}
      />
    </AuthLayout>
  )
}

export default WarehouseList